import { all, call, put, takeLatest } from 'redux-saga/effects';

import {
  acceptRequest,
  declineRequest,
  getToMsg,
  getToMsgDetail,
  getFromMsg,
  getFromMsgDetail,
  deleteRequest,
  setNotificationActivity,
} from './APIs/notification.js';
import types from '../constants/notifConstants.js';
import {
  setNotifFromAction,
  setNotifFromDetailAction,
  setNotifToAction,
  setNotifToDetailAction,
  setNotifToDetailErrorAction,
  setNotifFromDetailErrorAction,
  successNotif,
  failedNotif,
  setActivityTimeAction,
} from '../actions/notifActions.js';
import {
  selectLastNotificationFromListQuery,
  selectLastNotificationToListQuery,
} from './notificationListQuery.js';
import { getApiErrorMessage } from 'utils/apiError';

function* getNotifToSaga({ payload }) {
  try {
    const requestPayload = payload ?? (yield selectLastNotificationToListQuery());
    const data = yield getToMsg(requestPayload);
    yield put(setNotifToAction(data));
  } catch (error) {
    yield put(failedNotif(getApiErrorMessage(error, 'Failed to load outgoing notifications.')));
  }
}

function* getNotifFromSaga({ payload }) {
  try {
    const requestPayload = payload ?? (yield selectLastNotificationFromListQuery());
    const data = yield getFromMsg(requestPayload);
    yield put(setNotifFromAction(data));
  } catch (error) {
    yield put(failedNotif(getApiErrorMessage(error, 'Failed to load received notifications.')));
  }
}

function* getNotifToDetailSaga({ payload }) {
  try {
    const data = yield getToMsgDetail(payload);
    yield put(setNotifToDetailAction(data));
  } catch (error) {
    yield put(setNotifToDetailErrorAction());
    yield put(failedNotif(getApiErrorMessage(error, 'Failed to load notification detail.')));
  }
}

function* getNotifFromDetailSaga({ payload }) {
  try {
    const data = yield getFromMsgDetail(payload);
    yield put(setNotifFromDetailAction(data));
  } catch (error) {
    yield put(setNotifFromDetailErrorAction());
    yield put(failedNotif(getApiErrorMessage(error, 'Failed to load notification detail.')));
  }
}

function* refreshReceivedList() {
  try {
    const lastQuery = yield selectLastNotificationFromListQuery();
    const data = yield getFromMsg(lastQuery);
    yield put(setNotifFromAction(data));
  } catch (error) {
    yield put(failedNotif(getApiErrorMessage(error, 'Failed to refresh received notifications.')));
  }
}

function* refreshOutgoingList() {
  try {
    const lastQuery = yield selectLastNotificationToListQuery();
    const data = yield getToMsg(lastQuery);
    yield put(setNotifToAction(data));
  } catch (error) {
    yield put(failedNotif(getApiErrorMessage(error, 'Failed to refresh outgoing notifications.')));
  }
}

function* acceptRequestSaga({ payload }) {
  try {
    yield acceptRequest(payload);
    yield call(refreshReceivedList);
    yield put(successNotif('Accept Success!'));
  } catch (error) {
    yield put(failedNotif(getApiErrorMessage(error, 'Failed to accept request.')));
  }
}

function* declineRequestSaga({ payload }) {
  try {
    yield declineRequest(payload);
    yield call(refreshReceivedList);
    yield put(successNotif('Decline Successful!'));
  } catch (error) {
    yield put(failedNotif(getApiErrorMessage(error, 'Failed to decline request.')));
  }
}

function* deleteRequestSaga({ payload }) {
  try {
    yield deleteRequest(payload);
    yield call(refreshOutgoingList);
    yield put(successNotif('Delete Successful!'));
  } catch (error) {
    yield put(failedNotif(getApiErrorMessage(error, 'Failed to delete notification.')));
  }
}

function* onGetNotifToMsg() {
  yield takeLatest(types.GET_NOTIF_TO, getNotifToSaga);
}

function* onGetNotifFromMsg() {
  yield takeLatest(types.GET_NOTIF_FROM, getNotifFromSaga);
}

function* onGetNotifToDetail() {
  yield takeLatest(types.GET_NOTIF_TO_DETAIL, getNotifToDetailSaga);
}

function* onGetNotifFromDetail() {
  yield takeLatest(types.GET_NOTIF_FROM_DETAIL, getNotifFromDetailSaga);
}

function* onAcceptRequest() {
  yield takeLatest(types.ACCEPT_REQUEST, acceptRequestSaga);
}

function* onDeclineRequest() {
  yield takeLatest(types.DECLINE_REQUEST, declineRequestSaga);
}

function* onDeleteRequest() {
  yield takeLatest(types.DELETE_REQUEST, deleteRequestSaga);
}

function* setNotificationActivitySaga() {
  try {
    const response = yield setNotificationActivity();
    yield put(setActivityTimeAction(response));
  } catch (error) {
    yield put(failedNotif(getApiErrorMessage(error, 'Failed to refresh notifications.')));
  }
}

function* onSetNotificationActivity() {
  yield takeLatest(types.SET_NOTIF_ACTIVITY, setNotificationActivitySaga);
}

export default function* notificationSaga() {
  yield all([
    call(onGetNotifToMsg),
    call(onGetNotifFromMsg),
    call(onGetNotifToDetail),
    call(onGetNotifFromDetail),
    call(onAcceptRequest),
    call(onDeclineRequest),
    call(onDeleteRequest),
    call(onSetNotificationActivity),
  ]);
}
